import { useEffect, useMemo, useState } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Environment, Grid, Edges, ContactShadows } from '@react-three/drei';
import { Box3, Material, Texture, Vector3 } from 'three';
import { ehRebaixada, ehTexturaBase, materialAcm, materialAluminio, materialFerragem } from '../../lib/acmMateriais3d';
import { CORES, CORES_PADRAO } from '../../lib/calculoAcm';
import { construirCena3D, coresAlisar, AcmDoor3DParams, AluBox3D, SheetPanel3D, HardwarePiece3D } from '../../lib/acm3d';
import { Selecao, alvoDoPainel, painelNaSelecao } from '../../lib/acmEditor';

const MM = 0.001; // converte mm -> metros para a cena three.js

interface Props {
    params: AcmDoor3DParams;
    /** Modo editor: peças da seleção ganham contorno e um clique numa chapa seleciona o elemento. */
    selecao?: Selecao | null;
    /** Folha em edição (conjunto): só as peças dela ganham contorno. Um clique numa peça de outra folha a escolhe. */
    folhaAtiva?: string;
    onSelecionar?: (s: Selecao | null, folha?: string) => void;
}

type Camada = 'estrutura' | 'chapaFrente' | 'chapaVerso' | 'ferragens' | 'marco' | 'bordasU';

/** Material criado uma vez por peça e descartado ao desmontar (texturas-base ficam em cache). */
function useMaterial<T extends Material>(criar: () => T, deps: unknown[]): T {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const mat = useMemo(criar, deps);
    useEffect(() => () => {
        const m = mat as unknown as { map?: Texture | null; roughnessMap?: Texture | null; dispose: () => void };
        if (m.map && !ehTexturaBase(m.map)) m.map.dispose();
        if (m.roughnessMap && !ehTexturaBase(m.roughnessMap)) m.roughnessMap.dispose();
        m.dispose();
    }, [mat]);
    return mat;
}

function AluMesh({ box, explodido, offsetZ, corMarco }: { box: AluBox3D; explodido: boolean; offsetZ: number; corMarco?: { nome: string; hex: string } }) {
    const [cx, cy, cz] = box.center;
    const [sx, sy, sz] = box.size;
    const z = (cz + (explodido ? offsetZ : 0)) * MM;
    // Marco revestido em ACM (cor do marco) quando as chapas estão à mostra; senão, alumínio escovado (vista técnica)
    const material = useMaterial(
        () => (corMarco ? materialAcm(corMarco.nome, corMarco.hex, sx * MM, sy * MM) : materialAluminio()),
        [corMarco?.nome, corMarco?.hex, sx, sy],
    );
    return (
        <mesh position={[cx * MM, cy * MM, z]} castShadow receiveShadow material={material}>
            <boxGeometry args={[sx * MM, sy * MM, sz * MM]} />
            {!corMarco && <Edges color="#6b6b72" />}
        </mesh>
    );
}

function PanelMesh({ panel, explodido, offsetZ, selecionado, onSelecionar }: { panel: SheetPanel3D; explodido: boolean; offsetZ: number; selecionado?: boolean; onSelecionar?: (s: Selecao | null, folha?: string) => void }) {
    const [cx, cy, cz] = panel.center;
    const [sx, sy, sz] = panel.size;
    const axis = panel.explodeAxis ?? 'z';
    const dir = panel.explodeDir ?? (cz >= 0 ? 1 : -1);
    const offset = explodido ? offsetZ * dir : 0;
    const x = (cx + (axis === 'x' ? offset : 0)) * MM;
    const z = (cz + (axis === 'z' ? offset : 0)) * MM;
    const material = useMaterial(
        () => materialAcm(panel.corNome, panel.corHex, sx * MM, sy * MM, ehRebaixada(panel.id)),
        [panel.corNome, panel.corHex, sx, sy, panel.id],
    );
    return (
        <mesh position={[x, cy * MM, z]} castShadow receiveShadow material={material}
            onClick={onSelecionar ? (e) => { const alvo = alvoDoPainel(panel.id); if (alvo) { e.stopPropagation(); onSelecionar(alvo.selecao, alvo.folha); } } : undefined}>
            <boxGeometry args={[sx * MM, sy * MM, sz * MM]} />
            {selecionado && <Edges color="#2563eb" threshold={15} />}
        </mesh>
    );
}

function HardwareMesh({ piece, explodido, offsetZ, corKit }: { piece: HardwarePiece3D; explodido: boolean; offsetZ: number; corKit?: string }) {
    const [cx, cy, cz] = piece.center;
    const [sx, sy, sz] = piece.size;
    const z = (cz + (explodido ? offsetZ * 1.4 * (cz < 0 ? -1 : 1) : 0)) * MM;
    const material = useMaterial(() => materialFerragem(piece.acabamento, corKit), [piece.acabamento, corKit]);
    if (piece.forma === 'cilindro') {
        const eixo = piece.eixo || 'y';
        const raio = ((eixo === 'x' ? sy : sx) / 2) * MM;
        const comp = (eixo === 'x' ? sx : eixo === 'y' ? sy : sz) * MM;
        const rot: [number, number, number] = eixo === 'z' ? [Math.PI / 2, 0, 0] : eixo === 'x' ? [0, 0, Math.PI / 2] : [0, 0, 0];
        return (
            <mesh position={[cx * MM, cy * MM, z]} rotation={rot} castShadow receiveShadow material={material}>
                <cylinderGeometry args={[raio, raio, comp, 28]} />
            </mesh>
        );
    }
    return (
        <mesh position={[cx * MM, cy * MM, z]} castShadow receiveShadow material={material}>
            <boxGeometry args={[sx * MM, sy * MM, sz * MM]} />
        </mesh>
    );
}

function Cena({ params, explodido, camadas, mostrarVao, selecao, folhaAtiva, onSelecionar }: { params: AcmDoor3DParams; explodido: boolean; camadas: Record<Camada, boolean>; mostrarVao: boolean; selecao?: Selecao | null; folhaAtiva?: string; onSelecionar?: (s: Selecao | null, folha?: string) => void }) {
    const cena = useMemo(() => construirCena3D(params), [params]);

    const bounds = useMemo(() => {
        const box = new Box3();
        box.expandByPoint(new Vector3(0, 0, 0));
        box.expandByPoint(new Vector3(cena.vao.largura * MM, cena.vao.altura * MM, 0));
        return box;
    }, [cena]);

    const nomeMarco = params.cores?.U || CORES_PADRAO.U;
    const corMarco = camadas.bordasU ? { nome: nomeMarco, hex: CORES[nomeMarco] || '#1a1a1a' } : undefined;
    // Alisar na cor predominante de cada lado; a virada (dentro do vão) acompanha o lado A
    const corAlisar = useMemo(() => coresAlisar(cena, { nome: nomeMarco, hex: CORES[nomeMarco] || '#1a1a1a' }), [cena, nomeMarco]);

    const centerX = (bounds.min.x + bounds.max.x) / 2;
    const explodeOffset = explodido ? Math.max(cena.folha.largura, cena.folha.altura) * 0.14 : 0;

    return (
        <group position={[-centerX, 0, 0]}>
            {mostrarVao && camadas.marco && cena.marco.map(b => <AluMesh key={b.id} box={b} explodido={explodido} offsetZ={-explodeOffset * 1.8} corMarco={corMarco} />)}
            {mostrarVao && camadas.marco && cena.alisar?.map(b => <AluMesh key={b.id} box={b} explodido={false} offsetZ={0} corMarco={b.id.endsWith('-B') ? corAlisar.B : corAlisar.A} />)}
            {camadas.estrutura && cena.estrutura.map(b => <AluMesh key={b.id} box={b} explodido={explodido} offsetZ={0} />)}
            {camadas.chapaFrente && cena.chapasFrente.map(p => <PanelMesh key={p.id} panel={p} explodido={explodido} offsetZ={explodeOffset} selecionado={painelNaSelecao(p.id, selecao, folhaAtiva)} onSelecionar={onSelecionar} />)}
            {camadas.chapaVerso && cena.chapasVerso.map(p => <PanelMesh key={p.id} panel={p} explodido={explodido} offsetZ={explodeOffset} selecionado={painelNaSelecao(p.id, selecao, folhaAtiva)} onSelecionar={onSelecionar} />)}
            {camadas.bordasU && cena.bordasU.map(p => <PanelMesh key={p.id} panel={p} explodido={explodido} offsetZ={explodeOffset} />)}
            {camadas.ferragens && cena.ferragens.map(h => <HardwareMesh key={h.id} piece={h} explodido={explodido} offsetZ={explodeOffset} corKit={params.corKit} />)}
        </group>
    );
}

/** Acompanha o tema do sistema (classe `dark` no <html>), inclusive quando o usuário alterna o tema. */
function useTemaEscuro() {
    const [escuro, setEscuro] = useState(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
    useEffect(() => {
        const el = document.documentElement;
        const obs = new MutationObserver(() => setEscuro(el.classList.contains('dark')));
        obs.observe(el, { attributes: true, attributeFilter: ['class'] });
        return () => obs.disconnect();
    }, []);
    return escuro;
}

/** Reposiciona a câmera quando o tamanho do vão muda (a prop `camera` do Canvas só vale na montagem). */
function AjusteCamera({ dist, alvoY }: { dist: number; alvoY: number }) {
    const camera = useThree(st => st.camera);
    const controls = useThree(st => st.controls) as { target: Vector3; update: () => void } | null;
    const invalidate = useThree(st => st.invalidate);
    useEffect(() => {
        camera.position.set(dist * 0.9, alvoY + dist * 0.3, dist * 0.9);
        camera.lookAt(0, alvoY, 0);
        if (controls) {
            controls.target.set(0, alvoY, 0);
            controls.update();
        }
        invalidate();
    }, [dist, alvoY, camera, controls, invalidate]);
    return null;
}

const MODOS_PRESET: { id: string; label: string; titulo: string; camadas: Record<Camada, boolean> }[] = [
    { id: 'completa', label: 'Completa', titulo: 'Porta completa: marco, estrutura, chapas e ferragens', camadas: { marco: true, estrutura: true, chapaFrente: true, chapaVerso: true, bordasU: true, ferragens: true } },
    { id: 'estrutura', label: 'Estrutura', titulo: 'Só os perfis de alumínio (sem chapas)', camadas: { marco: true, estrutura: true, chapaFrente: false, chapaVerso: false, bordasU: false, ferragens: false } },
    { id: 'chapas', label: 'Chapas', titulo: 'Só as chapas de ACM', camadas: { marco: false, estrutura: false, chapaFrente: true, chapaVerso: true, bordasU: true, ferragens: false } },
    { id: 'frente', label: 'Lado A', titulo: 'Só o lado A (frente)', camadas: { marco: true, estrutura: true, chapaFrente: true, chapaVerso: false, bordasU: true, ferragens: true } },
    { id: 'verso', label: 'Lado B', titulo: 'Só o lado B (verso)', camadas: { marco: true, estrutura: true, chapaFrente: false, chapaVerso: true, bordasU: true, ferragens: true } },
];

export function AcmDoor3DViewer({ params, selecao, folhaAtiva, onSelecionar }: Props) {
    const [presetId, setPresetId] = useState('completa');
    const [explodido, setExplodido] = useState(false);
    const [mostrarVao, setMostrarVao] = useState(true);
    const preset = MODOS_PRESET.find(p => p.id === presetId) || MODOS_PRESET[0];
    const escuro = useTemaEscuro();

    const vaoM = params.vaoAltura ? params.vaoAltura * MM : 3;
    const vaoLarguraM = params.vaoLargura ? params.vaoLargura * MM : 1.2;
    const camDist = Math.max(2.2, Math.max(vaoM, vaoLarguraM * 1.4) * 1.6);

    const chip = 'rounded-full px-3 py-1 text-xs font-medium transition-colors';
    return (
        <div className="relative flex flex-col h-full min-h-0">
            {/* controles flutuantes sobre a cena */}
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex flex-wrap items-start justify-between gap-2 p-2.5">
                <div role="group" aria-label="Vista" className="pointer-events-auto inline-flex max-w-full overflow-x-auto rounded-full bg-white/90 p-0.5 shadow-md ring-1 ring-slate-200 backdrop-blur dark:bg-slate-900/90 dark:ring-slate-700">
                    {MODOS_PRESET.map(p => (
                        <button key={p.id} type="button" onClick={() => setPresetId(p.id)} title={p.titulo} aria-pressed={presetId === p.id}
                            className={`${chip} shrink-0 whitespace-nowrap ${presetId === p.id ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                            {p.label}
                        </button>
                    ))}
                </div>
                <div className="pointer-events-auto inline-flex gap-1 rounded-full bg-white/90 p-0.5 shadow-md ring-1 ring-slate-200 backdrop-blur dark:bg-slate-900/90 dark:ring-slate-700">
                    <button type="button" onClick={() => setExplodido(v => !v)} aria-pressed={explodido} title="Afasta as camadas para ver cada peça"
                        className={`${chip} ${explodido ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                        Explodir
                    </button>
                    <button type="button" onClick={() => setMostrarVao(v => !v)} aria-pressed={mostrarVao} title="Mostra ou esconde o marco e o vão da parede"
                        className={`${chip} ${mostrarVao ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                        Vão
                    </button>
                </div>
            </div>
            <p className="pointer-events-none absolute inset-x-0 bottom-2 z-10 text-center text-[11px] text-slate-500/80 dark:text-slate-400/80">
                Arraste para girar · role para aproximar{onSelecionar ? ' · clique numa chapa para editar' : ''}
            </p>

            <div className="flex-1 min-h-0 touch-none">
                <Canvas
                    shadows
                    camera={{ position: [camDist * 0.9, vaoM / 2 + camDist * 0.3, camDist * 0.9], fov: 40 }}
                    style={{ touchAction: 'none' }}
                    onPointerMissed={onSelecionar ? () => onSelecionar({ tipo: 'porta' }) : undefined}
                >
                    <color attach="background" args={[escuro ? '#3a3631' : '#d9d5cf']} />
                    <ambientLight intensity={escuro ? 0.7 : 0.8} />
                    <hemisphereLight args={['#fffaf2', escuro ? '#8a8378' : '#a8a29e', 0.9]} />
                    <directionalLight position={[-3, 4.5, 4]} intensity={2.2} castShadow shadow-mapSize={[2048, 2048]} shadow-bias={-0.0002}
                        shadow-camera-left={-4} shadow-camera-right={4} shadow-camera-top={5} shadow-camera-bottom={-2} />
                    <directionalLight position={[3, 2.5, -4]} intensity={0.8} />
                    <Environment preset="city" environmentIntensity={escuro ? 1 : 1.1} />
                    <ContactShadows position={[0, 0.001, 0]} opacity={escuro ? 0.5 : 0.35} scale={8} blur={2.6} far={3.5} resolution={1024} color="#000000" />
                    <Grid args={[10, 10]} position={[0, -0.01, 0]} cellColor={escuro ? '#524c45' : '#bfb9b1'} sectionColor={escuro ? '#6f685f' : '#9a938a'} fadeDistance={12} infiniteGrid />
                    <Cena params={params} explodido={explodido} camadas={preset.camadas} mostrarVao={mostrarVao} selecao={selecao} folhaAtiva={folhaAtiva} onSelecionar={onSelecionar} />
                    <AjusteCamera dist={camDist} alvoY={vaoM / 2} />
                    <OrbitControls makeDefault target={[0, vaoM / 2, 0]} enableDamping dampingFactor={0.08} minDistance={0.6} maxDistance={camDist * 4} maxPolarAngle={Math.PI / 2 - 0.02} />
                </Canvas>
            </div>
        </div>
    );
}
